import {
    Box,
    IconButton,
    List,
    ListItem,
    ListItemAvatar,
    ListItemSecondaryAction,
    ListItemText,
    Skeleton,
} from '@mui/material';
import React from 'react';
import { useGetAllAssetsQuery } from '../../../../../app/Slices/SevenDayApiSlice';
import { AddOn, FormValues as Experience } from '../../../../types/experience';
import { ExperienceType } from '../../../../enums/experience';
import { AddCircle } from '@mui/icons-material';
import { getPriceRange } from '../../../Experiences/ExperienceCard';

/** Approximate row height (avatar + padding + divider) for scroll cap. */
const ADDON_LIST_ITEM_HEIGHT = 96;

const AddOnList = ({
    experience,
    handleSelect,
    maxVisibleItems,
}: {
    experience: Experience;
    handleSelect: (experience: Experience) => void;
    maxVisibleItems?: number;
}) => {
    const { data: experiences, isLoading } = useGetAllAssetsQuery({
        url: `/modules`,
        type: [ExperienceType.Activity, ExperienceType.Vehicle],
    });

    const experienceMap: Map<number, Experience> = new Map(
        experiences?.map((experience) => [experience.id, experience]),
    );

    const visibleAddOnCount = experience.addOns.filter((addOn) =>
        experienceMap.has(addOn.id),
    ).length;
    const shouldScroll =
        maxVisibleItems != null && visibleAddOnCount > maxVisibleItems;

    const list = (
        <List disablePadding>
            {experience.addOns.map((addOn: Required<AddOn>, index) => {
                const addOnExperience = experienceMap.get(addOn.id);
                if (!addOnExperience) return;

                const { minPrice } = getPriceRange(addOnExperience);

                return (
                    <ListItem
                        key={addOn.id}
                        disableGutters
                        divider={index !== experience.addOns.length - 1}
                    >
                        <ListItemAvatar sx={{ pr: 1 }}>
                            <img
                                src={addOnExperience.photos[0].src}
                                style={{
                                    aspectRatio: 1,
                                    objectFit: 'cover',
                                    maxHeight: 75,
                                    borderRadius: 8,
                                }}
                            />
                        </ListItemAvatar>
                        <ListItemText
                            primary={addOnExperience.name}
                            secondary={minPrice ? `From $${minPrice}` : 'Free'}
                            primaryTypographyProps={{
                                sx: { fontWeight: 'bold' },
                            }}
                        />
                        <ListItemSecondaryAction>
                            <IconButton
                                color='primary'
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleSelect(addOnExperience);
                                }}
                            >
                                <AddCircle />
                            </IconButton>
                        </ListItemSecondaryAction>
                    </ListItem>
                );
            })}
        </List>
    );

    return (
        <>
            {isLoading ? (
                <SkeletonList />
            ) : shouldScroll ? (
                <Box
                    sx={{
                        maxHeight: ADDON_LIST_ITEM_HEIGHT * maxVisibleItems,
                        overflowY: 'auto',
                        overflowX: 'hidden',
                        pr: 0.5,
                    }}
                >
                    {list}
                </Box>
            ) : (
                list
            )}
        </>
    );
};

const SkeletonList = () => {
    return (
        <>
            <List>
                <ListItem divider disableGutters>
                    <ListItemAvatar sx={{ pr: 1 }}>
                        <Skeleton
                            variant='rounded'
                            height={75}
                            width={75}
                            sx={{ borderRadius: 2 }}
                        />
                    </ListItemAvatar>
                    <div>
                        <Skeleton variant='text' width={250} />
                        <Skeleton variant='text' width={100} />
                    </div>
                </ListItem>
                <ListItem disableGutters>
                    <ListItemAvatar sx={{ pr: 1 }}>
                        <Skeleton
                            variant='rounded'
                            height={75}
                            width={75}
                            sx={{ borderRadius: 2 }}
                        />
                    </ListItemAvatar>
                    <div>
                        <Skeleton variant='text' width={250} />
                        <Skeleton variant='text' width={100} />
                    </div>
                </ListItem>
            </List>
        </>
    );
};

export default AddOnList;
